import { Vector3 } from 'three';
import { clamp, normalizedClamp, normalizedOr } from '../core/math';
import { PITCH } from '../core/constants';
import {
  applyPunch,
  humanPlayerCommands,
  isBackPassTo,
  nearestOpponentWithin,
  playerCommands,
  canShield,
  punchVictim,
  type HumanActionIntent,
  type HumanPlayerIntent,
} from '../sim/controller';
import type { Match, MatchPlayer } from '../sim/match';
import {
  DRIBBLE_VELOCITY,
  SPRINT_VELOCITY,
  WALK_SPRINT_SWITCH,
  WALK_VELOCITY,
} from '../sim/anim/velocity';
import {
  BrowserInputSource,
  emptyTickInput,
  type InputButton,
  type JoinedController,
  type TickInput,
  type TickInputSource,
} from './device';

const ACTION_BUTTONS = ['shortPass', 'longPass', 'highPass', 'shot'] as const;
type ActionButton = (typeof ACTION_BUTTONS)[number] | 'flyingKick' | 'punch' | 'feint';

/** How long one line of the learning caption stays up. */
const HINT_MS = 1400;

/**
 * The team-mate a wall pass could still be returned by: the live half of `applyAttackingRun`'s
 * own nomination ("SEND HIM ON A RUN"), read from the passer's own side.
 *
 * Shared by the caption below and by `armWallPassReturn`, so what the caption promises a short
 * pass will do is exactly what pressing it then arms — never two independent copies of the same
 * three conditions drifting apart.
 */
function liveAttackingRunTarget(match: Match, player: MatchPlayer): MatchPlayer | null {
  const team = match.team(player.teamId);
  const target = team.attackingRunPlayer;
  if (!target || !target.active || target === player || team.attackingRunUntilMs <= match.timeMs) {
    return null;
  }
  return target;
}

/**
 * What a press just did, in the situation it was made in.
 *
 * Every line is the truth about this press and not a description of the key, which is the
 * whole point: the same key passes and closes down, and three of them are dead while
 * defending. Saying "SHOT" when nothing happened would be worse than saying nothing.
 */
function describePress(
  match: Match,
  player: MatchPlayer,
  input: TickInput,
  attacking: boolean,
): string | null {
  const pressed = (button: InputButton): boolean => input.pressed.has(button);

  if (match.inSetPiece) {
    const taker = match.setPieceTaker === player;
    if (pressed('shortPass')) {
      return taker ? 'PLAY THE BALL — THE MATCH RESTARTS' : 'NOT YOURS TO TAKE — WAIT FOR IT';
    }
    // Every action key plays the ball at a restart, not only the pass key: `captureAction`
    // forces `attacking` true whenever `match.inSetPiece` (below), so shortPass/longPass/
    // highPass/shot are all live for the taker, and `humanStrikeCommand` aims every one of them
    // with the arrows instead of the goal-seeking assist open play uses. The caption used to
    // claim only the pass key worked here; that was never true of the mechanics, only of the
    // wording.
    if (pressed('longPass')) {
      return taker ? 'THROUGH BALL — AIM IT WITH THE ARROWS' : 'NOBODY MAY MOVE AT A RESTART';
    }
    if (pressed('highPass')) {
      return taker ? 'HIGH PASS — AIM IT WITH THE ARROWS' : 'NOBODY MAY MOVE AT A RESTART';
    }
    if (pressed('shot')) {
      return taker ? 'SHOT — AIM IT WITH THE ARROWS' : 'NOBODY MAY MOVE AT A RESTART';
    }
    // These three are genuinely dead at a restart, unlike the four above: `special` only means
    // anything to a carrying touch, which the taker's queue never builds
    // (`humanPlayerCommands`'s set-piece branch is just the strike plus a walk onto the ball),
    // and `sprint`/`dribble` never reach the fixed WALK_VELOCITY that walk uses.
    for (const button of ['special', 'sprint', 'dribble'] as const) {
      if (pressed(button)) {
        return taker
          ? 'NO KNOCK-ON AT A RESTART — AN ACTION KEY PLAYS IT'
          : 'NOBODY MAY MOVE AT A RESTART';
      }
    }
    if (pressed('teamPressure') || pressed('keeperRush') || pressed('shirtPull')) {
      return 'NOT WHILE PLAY IS STOPPED';
    }
    if (pressed('allOut') || pressed('punch')) return 'NOT WHILE PLAY IS STOPPED';
    if (pressed('feint') || pressed('shield') || pressed('chip')) return 'NOT WHILE PLAY IS STOPPED';
    if (pressed('flyingKick')) return 'NOT WHILE PLAY IS STOPPED';
    if (pressed('switch')) return 'CHANGE PLAYER';
    return null;
  }

  if (pressed('switch')) {
    return player.hasPossession ? 'SEND HIM ON A RUN' : 'CHANGE PLAYER';
  }
  if (pressed('sprint')) return 'SPRINT';
  if (pressed('dribble')) return 'SLOW DOWN AND KEEP IT CLOSE';
  if (pressed('teamPressure')) return 'A TEAM-MATE GOES AT THE BALL';
  if (pressed('keeperRush')) return 'THE GOALKEEPER COMES OUT';
  if (pressed('allOut')) return 'ALL OUT — YOUR SIDE GOES IN HARDER (HOLD)';
  if (pressed('flyingKick')) return 'FLYING KICK — A RED CARD IF IT LANDS';
  if (pressed('punch')) {
    return punchVictim(match, player)
      ? 'HE PULLS UP AND SWINGS — A STRAIGHT RED'
      : 'NOBODY CLOSE ENOUGH TO HIT';
  }
  if (pressed('shirtPull')) {
    return nearestOpponentWithin(match, player, SHIRT_PULL_RANGE, player.directionVec)
      ? 'HOLDING HIS SHIRT — THAT IS A FREE KICK'
      : 'NOBODY CLOSE ENOUGH TO HOLD';
  }
  if (pressed('feint')) {
    if (!player.hasBestPossession) return 'BODY SWERVE — ONLY WITH THE BALL';
    return 'BODY SWERVE — HE SLOWS TO DRIBBLING PACE AND THROWS ONE';
  }
  if (pressed('shield')) {
    if (!player.hasBestPossession) return 'SHIELD THE BALL — ONLY WITH THE BALL';
    return canShield(match, player)
      ? 'SHIELDING IT — HE TURNS HIS BACK AND HOLDS HIM OFF (HOLD)'
      : 'NOBODY CLOSE ENOUGH TO HOLD OFF';
  }
  if (pressed('chip')) {
    if (!player.hasBestPossession) return 'CHIP THE KEEPER — ONLY WITH THE BALL';
    // The library has a forward high pass at a standstill, at a dribble and at a walk, and none
    // whatever at a sprint, so a man at full pelt asking for one selects nothing and looks to
    // him as though the key is dead. Saying so is cheaper than pretending otherwise.
    if (player.floatVelocity >= WALK_SPRINT_SWITCH) return 'NOT AT A SPRINT — STEADY YOURSELF';
    return 'CHIP — OVER THE KEEPER AND UNDER HIS OWN BAR';
  }

  if (attacking) {
    if (pressed('shortPass')) {
      return liveAttackingRunTarget(match, player)
        ? 'WALL PASS — HE PLAYS IT STRAIGHT BACK'
        : 'SHORT PASS';
    }
    if (pressed('longPass')) return 'THROUGH BALL';
    if (pressed('highPass')) return 'HIGH PASS — THIS IS THE CROSS';
    if (pressed('shot')) return 'SHOT';
    if (pressed('special')) {
      return player.hasPossession ? 'KNOCK IT AHEAD AND CHASE IT' : 'SPRINT (NO BALL TO KNOCK ON)';
    }
    return null;
  }

  if (pressed('highPass')) return 'SLIDING TACKLE';
  if (pressed('shortPass')) return 'CLOSE HIM DOWN';
  if (pressed('longPass')) return 'NOTHING — THE THROUGH BALL NEEDS THE BALL';
  if (pressed('shot')) return 'NOTHING — THE SHOT NEEDS THE BALL';
  if (pressed('special')) return 'SPRINT (NO BALL TO KNOCK ON)';
  return null;
}

interface ControllerBinding {
  device: JoinedController;
  selectedPlayerId: number;
  readonly input: HumanInputBuffer;
}

/**
 * Owns the device-to-player mapping for one match.
 *
 * The fixture's joined devices are copied verbatim. Browser gamepad discovery remains in the
 * shell; this class only samples the ids the shell handed to the match.
 */
export class PlayerInput {
  private readonly controllers: ControllerBinding[];
  private readonly source: TickInputSource;
  private readonly autoSwitchIndex = [0, 0];

  constructor(devices: readonly JoinedController[], source?: TickInputSource) {
    const joined = devices.filter((device) => device.side !== 0);
    this.controllers = joined.map((device) => ({
      device,
      selectedPlayerId: -1,
      input: new HumanInputBuffer(device.kind),
    }));
    this.source = source ?? new BrowserInputSource();
  }

  /**
   * Reassign devices to sides in the middle of a match.
   *
   * A device that keeps its side keeps the player it was driving, so switching somebody
   * else's controller does not yank yours off the man you were running with. A device moved
   * to the other side, or newly given one, picks a player on the next tick; one set to no
   * side hands his back to the computer.
   */
  setControllers(devices: readonly JoinedController[]): void {
    const previous = new Map(this.controllers.map((entry) => [entry.device.id, entry]));
    const joined = devices.filter((device) => device.side !== 0);
    this.controllers.length = 0;
    for (const device of joined) {
      const existing = previous.get(device.id);
      if (existing && existing.device.side === device.side) {
        existing.device = device;
        this.controllers.push(existing);
      } else {
        this.controllers.push({
          device,
          selectedPlayerId: -1,
          input: new HumanInputBuffer(device.kind),
        });
      }
    }
  }

  tick(match: Match): void {
    const samples = this.source.sample(this.controllers.map((controller) => controller.device));
    this.ensureSelections(match);
    this.releaseKeeper(match);
    this.autoSwitch(match);

    for (const controller of this.controllers) {
      const snapshot =
        samples.get(controller.device.id) ?? emptyTickInput(controller.device.kind);
      const selected = match.players.find((player) => player.id === controller.selectedPlayerId);
      if (!selected) continue;
      if (snapshot.pressed.has('switch')) this.manualSwitch(match, controller);
    }

    for (const controller of this.controllers) {
      const selected = match.players.find((player) => player.id === controller.selectedPlayerId);
      if (!selected) continue;
      const snapshot =
        samples.get(controller.device.id) ?? emptyTickInput(controller.device.kind);
      controller.input.tick(match, selected, snapshot);
      if (snapshot.held.has('switch') && selected.hasPossession) {
        applyAttackingRun(match, selected);
      }
      // The give half of a wall pass: a short pass — pressed, not held, so a mash on the key
      // does not re-arm it every tick it stays buffered — while this side's own nomination on
      // a team-mate is still live. `armWallPassReturn` checks the nomination itself; the two
      // gates here only keep it out of situations where the key means something else entirely
      // (taking a restart) or nothing at all (no ball to give).
      if (
        snapshot.pressed.has('shortPass') &&
        selected.hasBestPossession &&
        match.inPlay &&
        !match.inSetPiece
      ) {
        armWallPassReturn(match, selected);
      }
      // Both are orders to the rest of the side rather than to the man you are driving, so
      // they fire whatever he happens to be doing — but not while play is stopped
      // (`humancontroller.cpp:332-464`: the defensive buttons are dead in a set piece).
      //
      // They are re-given on every tick the key is down, not once when it goes down
      // (`humancontroller.cpp:199-233` re-issues the order for as long as the button is held).
      // Each order only lasts half a second and three tenths respectively, so firing once on
      // the press meant holding the key gave one short burst and then nothing, and the
      // nominated man wandered back to his position with the key still down. Re-giving it also
      // re-picks who goes, so the squeeze follows the ball carrier as he moves.
      if (match.inPlay && !match.inSetPiece) {
        if (orderGiven(snapshot, 'teamPressure')) {
          applyTeamPressure(match, selected.teamId, this.controlledIds());
        }
        if (orderGiven(snapshot, 'keeperRush')) applyKeeperRush(match, selected.teamId);
        // The third order, and the only one that goes to the whole side at once. Re-given every
        // tick the key is down, exactly like the other two, because it lasts half a second.
        if (orderGiven(snapshot, 'allOut')) applyAllOut(match, selected.teamId);
        // Held, not tapped: a shirt is grabbed and hung on to. The drag is applied every tick
        // the key is down, and the referee is told once per hold.
        if (snapshot.held.has('shirtPull')) applyShirtPull(match, selected);
        // The punch lands off the SWING and not off the key, so it is asked on every tick
        // rather than while something is held: `applyPunch` answers only on the one frame of
        // `special/punch` that the fist arrives on, and no key is still down by then. See
        // `punchConnects` for what went wrong when this was driven from the press instead.
        applyPunch(match, selected);
      }
    }
  }

  /**
   * Read the devices and throw the reading away.
   *
   * For ticks the match does not take, which is every tick while the game is paused. A press and
   * a release are each latched until something reads them, so a menu visited for ten seconds
   * used to bank every key touched in it and hand the whole lot over on the first tick after
   * play resumed — a shot, a slide and a switch all at once, from keys pressed while the pause
   * menu was up.
   */
  drain(): void {
    this.source.sample(this.controllers.map((controller) => controller.device));
  }

  commands(match: Match, player: MatchPlayer) {
    const controller = this.controllers.find(
      (candidate) => candidate.selectedPlayerId === player.id,
    );
    return controller
      ? humanPlayerCommands(match, player, controller.input.intent)
      : playerCommands(match, player);
  }

  /** The devices currently driving somebody, in the order they were joined. */
  controllerIds(): string[] {
    return this.controllers.map((controller) => controller.device.id);
  }

  /**
   * The newest "this is what that button just did" line, or null when there is none to show.
   *
   * Newest across every device, because two people sharing a keyboard and a pad would otherwise
   * take turns overwriting one another. It expires on the match's own clock, which keeps running
   * while play is stopped — a caption that froze with the football clock would sit there through
   * a whole corner.
   */
  hint(match: Match): string | null {
    let newest: { text: string; atMs: number } | null = null;
    for (const controller of this.controllers) {
      const candidate = controller.input.hint;
      if (candidate && (!newest || candidate.atMs > newest.atMs)) newest = candidate;
    }
    if (!newest || match.timeMs - newest.atMs > HINT_MS) return null;
    return newest.text;
  }

  /** The device driving this man, or null when the computer has him. */
  driverOf(player: MatchPlayer): JoinedController | null {
    return (
      this.controllers.find((controller) => controller.selectedPlayerId === player.id)?.device ??
      null
    );
  }

  controlledPlayer(deviceId: string, match: Match): MatchPlayer | null {
    const id =
      this.controllers.find((controller) => controller.device.id === deviceId)
        ?.selectedPlayerId ?? -1;
    return match.players.find((player) => player.id === id) ?? null;
  }

  dispose(): void {
    this.source.dispose?.();
  }

  private ensureSelections(match: Match): void {
    const selectedIds = new Set(
      this.controllers
        .map((controller) => controller.selectedPlayerId)
        .filter((id) => id >= 0),
    );
    for (const controller of this.controllers) {
      const current = match.players.find(
        (player) => player.id === controller.selectedPlayerId && player.active,
      );
      if (current) continue;
      selectedIds.delete(controller.selectedPlayerId);
      const teamId = controller.device.side === -1 ? 0 : 1;
      const target = closestAvailable(
        match.team(teamId).players,
        match.ball.position,
        selectedIds,
      );
      if (!target) continue;
      controller.selectedPlayerId = target.id;
      controller.input.switched(match.timeMs);
      selectedIds.add(target.id);
    }
  }

  /**
   * Hand a person whoever his side has sent for the ball, `Team::UpdateSwitch`
   * (`team.cpp:508-546`).
   *
   * Two rules, and the original ran only the second of them. That one waits until the ball is
   * already his and no opponent is contesting it, which is far too late to play it: a receiver
   * commits his touch up to a quarter of a second before the ball reaches his foot, and a pass
   * or a shot cannot be reconsidered once it has started — only running, trapping and
   * ball-control clips may be (`anim/runtime.ts`). So the computer had chosen and begun the
   * first-time pass before the person ever had the controls, and a person watched his own side
   * play the ball without him. Measured, not supposed.
   *
   * The first rule is the original author's own answer to that, sitting commented out beside it
   * under the heading "autoswitch on proximity" with the note "recently disabled"
   * (`team.cpp:522-530`). It hands the man over while the ball is still travelling to him:
   * within two seconds of reaching it, quickest of his side to it, and his side clearly winning
   * the race for it. The three thresholds are the original's, unchanged. Nothing says why it was
   * switched off, and what it fixes is plain, so it is on here.
   *
   * Whoever a person is driving is already favoured when a side elects its chaser
   * (`Match.updateDesignatedPlayer`), so control does not hop between men every few ticks once
   * it has landed.
   *
   * Neither rule ever gives away the goalkeeper. The original guards that on the second rule
   * only; a person handed the keeper while the keeper is forty metres up the pitch has lost the
   * match, so it is guarded on both.
   *
   * A THIRD guard, ours: neither rule may ever hand over the man a wall pass is armed to return
   * from. He is exactly who both rules above are built to grab — the ball heading straight at
   * him, almost certainly the quickest of his side to it, his side clearly winning the race —
   * so without this a wall pass reassigns the controller to him the instant the give is struck,
   * measured one tick after it, same as any other pass. That is two faults at once, not one.
   * First, the whole point of a one-two is that the passer keeps running into the space he just
   * made; being silently handed the receiver instead means the arrow keys that were driving that
   * run now drive a stranger, with no input the person actually meant for him. Second, worse:
   * `HumanInputBuffer` is a property of the CONTROLLER, not of the player it happens to be
   * pointed at, so the passer's own short-pass press — still buffered, since its clip has not
   * resolved yet — rides along onto the receiver and can fire again on his behalf, aimed by
   * `humanStrikeCommand`'s ordinary teammate search rather than back into the passer's run,
   * with nobody having asked him to play it. Leaving the receiver on the AI queue for the whole
   * window sidesteps both: `playerCommands` is what actually offers him the return
   * (`wallPassReturnCommand`, `src/sim/controller.ts`), and it can only do that while nobody's
   * controller has been pointed at him.
   */
  private autoSwitch(match: Match): void {
    for (const teamId of [0, 1] as const) {
      const teamControllers = this.controllers.filter(
        (controller) => (controller.device.side === -1 ? 0 : 1) === teamId,
      );
      if (teamControllers.length === 0) continue;
      const team = match.team(teamId);
      const target = team.designatedPlayer;
      // While play is stopped every time-to-ball is meaningless — nobody is allowed to move —
      // so the ball-is-on-its-way rule is live play only. A restart is covered by the other one.
      const ballIsOnItsWayToHim =
        match.inPlay &&
        !match.inSetPiece &&
        target.timeNeeded.usualMs < 2000 &&
        target.timeNeeded.usualMs <= team.timeNeededToGetToBallMs &&
        team.possessionAmount > 1.3;
      // The keeper is not handed over for a ball he is merely nearest to, and not for one he
      // has already gathered in his hands — his only move then is a throw, and the computer
      // knows how to make it. With the ball at his feet, which is what a back-pass leaves him,
      // he is a footballer like any other and the person gets him: a person who plays it back
      // to his own keeper and then watches the computer hoof it away has been robbed of his
      // own decision. `team.cpp:539` guards the keeper on every case; this guards him on the
      // two where the guard is doing something.
      const ballIsAtHisFeet = target.hasUniquePossession && match.ballRetainer !== target;
      // AND A BACK PASS ON ITS WAY TO HIM COUNTS TOO, which is the whole of the reported bug.
      // Waiting for the ball to be at his feet meant waiting for it to be 38 CENTIMETRES from
      // him: measured, a person played it back and spent the whole 2.8 seconds of the ball
      // rolling still driving an outfielder, and was given the keeper on the tick the ball
      // arrived — far too late to decide anything with it, because a receiver commits his touch
      // a quarter of a second early. He is handed over while it travels.
      //
      // Only for a ball he may NOT pick up, which is exactly a deliberate back pass. For a shot
      // or a cross he keeps his own diving and coming out, which is thrown away while a person
      // has him, and that is the reason the guard is here at all.
      const backPassComing =
        target.role === 'GK' && ballIsOnItsWayToHim && isBackPassTo(match, target);
      const isArmedWallPassReturn =
        team.wallPassReceiver === target && team.wallPassUntilMs > match.timeMs;
      if (
        !target.active ||
        (target.role === 'GK' && !ballIsAtHisFeet && !backPassComing) ||
        (!ballIsOnItsWayToHim && !target.hasUniquePossession && !match.inSetPiece) ||
        this.isControlled(target.id) ||
        isArmedWallPassReturn
      ) continue;
      const index = this.autoSwitchIndex[teamId]! % teamControllers.length;
      const controller = teamControllers[index]!;
      this.select(controller, target, match.timeMs);
      this.autoSwitchIndex[teamId] = (index + 1) % teamControllers.length;
    }
  }

  /**
   * Hand the keeper back the moment the ball stops being his.
   *
   * Nothing else would. Every hand-over rule moves a person ONTO somebody and none of them
   * moves him off, so a person given the keeper for a back-pass would still be driving him with
   * play at the other end — and a keeper is the one man on the pitch whose own behaviour, the
   * diving and the coming out, is thrown away while a person has him.
   *
   * He is kept while the ball is at his feet, and while his side still has him down as the man
   * going for it, which is what stops the control flickering off him between two touches of his
   * own dribble. He is given back the instant he picks it up, because a throw is the computer's
   * to make, and as soon as somebody else is going for the ball.
   */
  private releaseKeeper(match: Match): void {
    for (const controller of this.controllers) {
      const team = match.team(controller.device.side === -1 ? 0 : 1);
      const selected = team.players.find(
        (player) => player.id === controller.selectedPlayerId,
      );
      if (!selected || selected.role !== 'GK') continue;
      const keep =
        match.ballRetainer !== selected &&
        (selected.hasUniquePossession || team.designatedPlayer === selected);
      if (keep) continue;
      const designated = team.designatedPlayer;
      const target =
        designated.active && designated.role !== 'GK' && !this.isControlled(designated.id)
          ? designated
          : closestAvailable(team.players, match.ball.position, this.controlledIds());
      if (target) this.select(controller, target, match.timeMs);
    }
  }

  private manualSwitch(match: Match, controller: ControllerBinding): void {
    const current = match.players.find(
      (player) => player.id === controller.selectedPlayerId,
    );
    if (!current || current.hasUniquePossession) return;
    const team = match.team(current.teamId);
    const best = team.players
      .filter((player) => player.active)
      .reduce((candidate, player) =>
        player.timeNeeded.usualMs < candidate.timeNeeded.usualMs ? player : candidate,
      );
    if (
      current === best &&
      current === team.designatedPlayer &&
      team.possessionAmount >= 1
    ) return;

    let target: MatchPlayer | null = null;
    if (
      match.bestPossessionTeamId === team.id &&
      !this.isControlled(team.designatedPlayer.id)
    ) {
      target = team.designatedPlayer;
    } else if (
      match.bestPossessionTeamId === team.id &&
      !this.isControlled(best.id)
    ) {
      target = best;
    } else {
      target = bestSwitchTarget(match, current.teamId, this.controlledIds());
    }
    if (!target || target.role === 'GK') return;
    this.select(controller, target, match.timeMs);
  }

  private select(
    controller: ControllerBinding,
    player: MatchPlayer,
    timeMs: number,
  ): void {
    controller.selectedPlayerId = player.id;
    controller.input.switched(timeMs);
  }

  private controlledIds(): Set<number> {
    return new Set(this.controllers.map((controller) => controller.selectedPlayerId));
  }

  private isControlled(playerId: number): boolean {
    return this.controllers.some(
      (controller) => controller.selectedPlayerId === playerId,
    );
  }
}

export class HumanInputBuffer {
  private readonly kind: 'keyboard' | 'gamepad';
  private actionMode: 0 | 1 | 2 = 0;
  private actionButton: ActionButton | null = null;
  private gaugeMs = 0;
  private actionBufferMs = 0;
  private switchedAtMs = -1000;
  private previousTimeNeededMs = 0;
  intent: HumanPlayerIntent;
  /** What the last press did and when, for the learning caption. See `describePress`. */
  hint: { text: string; atMs: number } | null = null;

  constructor(kind: 'keyboard' | 'gamepad') {
    this.kind = kind;
    this.intent = {
      direction: new Vector3(0, -1, 0),
      velocity: 0,
      action: null,
      pressure: false,
      superCancel: false,
      keyboard: kind === 'keyboard',
      switchBias: 0,
      longTouch: false,
      shield: false,
      chip: false,
    };
  }

  /**
   * A charged action belongs to the man who was under this controller a moment ago, not to
   * whoever it points at next, so it is dropped here rather than carried over onto him.
   *
   * Without this a human's own pass could fire a SECOND, uncommanded one through the very
   * team-mate he had just passed to — measured, not supposed: a short pass struck at
   * `t=1130`, `autoSwitch` handed the controller to the receiver at `t=1140` (one tick later,
   * exactly as it is meant to — see its own doc above), and at `t=1400` the receiver played an
   * automatic short pass of his own, having never been pressed for.
   *
   * The mechanism is a one-tick race. A pass leaves the passer's foot on the tick his clip
   * reaches its touch frame, which is the same tick `autoSwitch` can first treat the ball as
   * his side's and hand the receiver the controller — deliberately fast, that being the whole
   * point of `autoSwitch`. But `clearCompletedAction` below only ever learns an action has
   * already played by reading the CURRENTLY SELECTED man's own clip, and `autoSwitch` runs
   * before that read on the very same tick. So the one tick on which the
   * passer's own state would have cleared it is skipped outright: from the tick of the switch
   * onward the buffer is ticked against the receiver, whose clip is never the charged type, so
   * the check can never match. Meanwhile `currentAction`'s readiness test only asks whether the
   * button is NOT currently held, which idle input satisfies on every following tick — so the
   * stale action stays armed for ever and fires the instant the receiver's own animation
   * reaches a frame that can play it: a team-mate who passes the ball on by himself.
   */
  switched(timeMs: number): void {
    this.switchedAtMs = timeMs;
    this.clearAction();
  }

  tick(match: Match, player: MatchPlayer, input: TickInput): void {
    this.clearCompletedAction(player);
    const attacking = match.inSetPiece || possessionContext(match, player, input) >= 0;
    // Read before the press latches are consumed by anything else, and always — the caption is
    // switched on and off in the shell, so nothing down here has to know whether it is showing.
    const said = describePress(match, player, input, attacking);
    if (said) this.hint = { text: said, atMs: match.timeMs };
    this.captureFlyingKick(input);
    this.capturePunch(input);
    this.captureFeint(input);
    this.captureAction(attacking, input);
    this.updateActionBuffer(match, player, input);

    let direction = new Vector3(input.x, input.y, 0);
    let velocity = 0;
    if (direction.lengthSq() === 0) {
      direction.copy(player.directionVec);
    } else {
      direction.normalize();
      velocity = input.held.has('sprint') || input.held.has('special')
        ? SPRINT_VELOCITY
        : input.held.has('dribble')
          ? DRIBBLE_VELOCITY
          : input.held.has('switch') && match.designatedPossessionPlayer === player
            ? 0
            : WALK_VELOCITY;
    }

    const lastSwitchBias =
      1 - normalizedClamp(match.timeMs - this.switchedAtMs, 0, 300);
    if (lastSwitchBias > 0) {
      const influence = Math.pow(lastSwitchBias, 0.7) * 0.5;
      const blended = player.directionVec
        .clone()
        .multiplyScalar(player.floatVelocity * influence)
        .addScaledVector(direction, velocity * (1 - influence));
      velocity = blended.length();
      direction = normalizedOr(blended, direction);
    }

    // What this person is asking for at this instant, taken fresh every tick and including the
    // just-switched blend above, which is what `HumanController::GetFloatVelocity` returns
    // (`humancontroller.cpp:471`, reading `_GetHidInput` at `:498-524`). A carrying touch blends
    // it with the wish its clip was started for, so how far he knocks the ball in front of him
    // answers to where his controls are NOW and not only to where they were half a second ago.
    player.controllerVelocityFloat = velocity;

    const defensivePressure =
      !attacking && this.actionMode !== 2 && input.held.has('shortPass');
    this.intent = {
      direction,
      velocity,
      action: this.currentAction(input),
      pressure: defensivePressure,
      superCancel: input.held.has('dribble') && input.held.has('sprint'),
      keyboard: this.kind === 'keyboard',
      switchBias: lastSwitchBias,
      // Held rather than tapped, because a carrying touch only comes round every few tenths of
      // a second: a tap between two of them would be swallowed and nothing would happen.
      longTouch: input.held.has('special'),
      shield: input.held.has('shield'),
      // Held for the same reason the two above are: an animation decision only comes round
      // every few tenths of a second, so a tap between two of them is simply never seen.
      chip: input.held.has('chip'),
    };
    this.previousTimeNeededMs = player.timeNeeded.usualMs;
  }

  private clearCompletedAction(player: MatchPlayer): void {
    if (this.actionMode === 0 || !this.actionButton) return;
    // A swing is over the moment it starts: the clip is a `special`, which the animation
    // runtime never reconsiders once it is running, so leaving the press buffered would only
    // queue a second punch behind the first when the clip ends.
    if (
      (this.actionButton === 'punch' || this.actionButton === 'feint') &&
      player.anim.currentFunctionType === 'special'
    ) {
      this.clearAction();
      return;
    }
    const type = actionType(this.actionMode, this.actionButton);
    if (
      player.anim.currentFunctionType === type &&
      !player.touchPending()
    ) {
      this.clearAction();
    }
  }

  private captureAction(attacking: boolean, input: TickInput): void {
    // Nothing is captured while an action is already being charged (`humancontroller.cpp:361`
    // gates the whole block on `actionMode == 0`). Without this a second press overwrote the
    // first and reset its gauge, and the shot-cancel rule below could never see a buffered
    // shot: tapping short pass to abort a shot re-armed the buffer as a short pass instead,
    // and out came a 0.01-power pass to nobody rather than nothing at all.
    if (this.actionMode !== 0) return;
    if (attacking) {
      for (const button of ACTION_BUTTONS) {
        if (!input.pressed.has(button)) continue;
        this.actionMode = 2;
        this.actionButton = button;
        this.gaugeMs = 10;
        this.actionBufferMs = input.released.has(button) ? 10 : 0;
        break;
      }
      return;
    }
    if (input.pressed.has('highPass')) {
      this.actionMode = 1;
      this.actionButton = 'highPass';
      this.gaugeMs = 10;
      this.actionBufferMs = 0;
    }
  }

  /**
   * The flying kick, which is not held to the attacking/defending split the rest of the buttons
   * live under.
   *
   * Every other action means one thing with the ball and another without it, because the
   * original has four buttons doing eight jobs. This one has a key of its own and means the
   * same thing whatever the state of the game — which is the point of giving it one.
   */
  private captureFlyingKick(input: TickInput): void {
    if (this.actionMode !== 0) return;
    if (!input.pressed.has('flyingKick')) return;
    this.actionMode = 1;
    this.actionButton = 'flyingKick';
    this.gaugeMs = 10;
    this.actionBufferMs = 0;
  }

  /**
   * The punch, on the same footing as the flying kick and for the same reason: it means one
   * thing, whatever the state of the game, and it has a key to itself.
   *
   * Behind the flying kick in this order, so that a person mashing both gets the leap. Neither
   * can overwrite the other — `actionMode` is only ever captured from zero.
   */
  private capturePunch(input: TickInput): void {
    if (this.actionMode !== 0) return;
    if (!input.pressed.has('punch')) return;
    this.actionMode = 1;
    this.actionButton = 'punch';
    this.gaugeMs = 10;
    this.actionBufferMs = 0;
  }

  /**
   * The body swerve, on the same footing as the other two keys of its own — one meaning,
   * whatever the state of the game — and behind both of them in this order for the same
   * reason: `actionMode` is only ever captured from zero, so a person mashing keys gets the
   * one that matters most, and a dummy matters less than a man being kicked.
   */
  private captureFeint(input: TickInput): void {
    if (this.actionMode !== 0) return;
    if (!input.pressed.has('feint')) return;
    this.actionMode = 1;
    this.actionButton = 'feint';
    this.gaugeMs = 10;
    this.actionBufferMs = 0;
  }

  private updateActionBuffer(
    match: Match,
    player: MatchPlayer,
    input: TickInput,
  ): void {
    if (this.actionMode === 0 || !this.actionButton) return;
    if (
      this.actionMode === 2 &&
      (this.actionButton === 'shot' || this.actionButton === 'highPass') &&
      input.pressed.has('shortPass') &&
      !match.inSetPiece
    ) {
      this.clearAction();
      return;
    }
    if (input.held.has(this.actionButton)) {
      this.gaugeMs = clamp(this.gaugeMs + 10, 10, 1000);
      this.actionBufferMs = 0;
    } else {
      this.actionBufferMs += 10;
    }
    const expired =
      (this.actionMode === 2 &&
        (this.actionBufferMs > 2000 ||
          (this.previousTimeNeededMs > 0 &&
            player.timeNeeded.usualMs > this.previousTimeNeededMs + 700) ||
          player.anim.currentFunctionType === 'interfere')) ||
      (this.actionMode === 1 && this.actionBufferMs > 1000);
    if (expired) this.clearAction();
  }

  private currentAction(input: TickInput): HumanActionIntent | null {
    if (this.actionMode === 0 || !this.actionButton) return null;
    if (this.actionMode === 1) {
      if (this.actionButton === 'flyingKick') return { type: 'flyingkick', power: 1 };
      if (this.actionButton === 'punch') return { type: 'punch', power: 1 };
      if (this.actionButton === 'feint') return { type: 'feint', power: 1 };
      return this.actionButton === 'highPass' ? { type: 'sliding', power: 1 } : null;
    }
    const ready =
      !input.held.has(this.actionButton) ||
      this.gaugeMs > 500 ||
      this.actionBufferMs > 0;
    if (!ready) return null;
    const gauge = clamp((this.gaugeMs - 60) / (1000 - 60), 0, 1);
    const exponent =
      this.actionButton === 'shortPass'
        ? 0.7
        : this.actionButton === 'longPass'
          ? 0.65
          : this.actionButton === 'highPass'
            ? 0.55
            : 0.6;
    return {
      type: actionType(2, this.actionButton),
      power: clamp(Math.pow(gauge, exponent), 0.01, 1),
    };
  }

  private clearAction(): void {
    this.actionMode = 0;
    this.actionButton = null;
    this.gaugeMs = 0;
    this.actionBufferMs = 0;
  }
}

/**
 * Whether the buttons mean attack or defence this tick, `humancontroller.cpp:364-381`.
 *
 * The same physical buttons pass and shoot when the ball is yours and tackle and close down
 * when it is not, so this number decides what a press means before the press is read.
 */
function possessionContext(
  match: Match,
  player: MatchPlayer,
  input: TickInput,
): number {
  if (match.designatedPossessionPlayer === player) return 1;
  let result = ownPossessionAmount(match, player) - 1;
  if (input.held.has('shortPass')) result += 0.15;
  if (input.held.has('longPass')) result += 0.15;
  if (input.held.has('shot')) result += 0.15;
  if (input.held.has('shortPass')) result -= 0.15;
  if (input.held.has('highPass')) result -= 0.15;
  if (match.ball.predict(0, new Vector3()).z > 1.5) result += 0.2;
  return result;
}

/**
 * How well placed *this* player is to get the ball, `playercontroller.cpp:616`.
 *
 * Not the same number as his side's, which is what this used to read: the team ratio
 * (`team.cpp:324`) takes the quickest man on each side and softens the comparison with a
 * 1500 ms offset, while the controller's own ratio divides the opposition's best time by this
 * one player's, with a 200 ms offset. Reading the team's handed the attacking buttons to a
 * defender standing 25 m off the ball whenever any team-mate of his was quickest to it — so
 * the slide key buffered a high pass he could never play and the close-down modifier was
 * never set. `src/sim/controller.ts` keeps a private copy of the same expression for the AI.
 */
function ownPossessionAmount(match: Match, player: MatchPlayer): number {
  return (
    (match.opponentTeam(player.teamId).timeNeededToGetToBallMs + 200) /
    (player.timeNeeded.usualMs + 200)
  );
}

/**
 * Whether an order to the rest of the side stands this tick.
 *
 * Down now, or tapped and released wholly between two ticks — the same latch the direction
 * reader uses (`device.ts`), so a fast tap is not swallowed by the fixed 100 Hz sampling.
 */
function orderGiven(
  input: TickInput,
  button: 'teamPressure' | 'keeperRush' | 'allOut',
): boolean {
  return input.held.has(button) || input.pressed.has(button);
}

function actionType(
  mode: 1 | 2,
  button: ActionButton,
): HumanActionIntent['type'] {
  if (mode === 1) {
    if (button === 'flyingKick') return 'flyingkick';
    if (button === 'punch') return 'punch';
    if (button === 'feint') return 'feint';
    return 'sliding';
  }
  if (button === 'shortPass') return 'shortpass';
  if (button === 'longPass') return 'longpass';
  if (button === 'highPass') return 'highpass';
  return 'shot';
}

function closestAvailable(
  players: readonly MatchPlayer[],
  target: Vector3,
  excluded: ReadonlySet<number>,
): MatchPlayer | null {
  return (
    players
      .filter(
        (player) =>
          player.active && player.role !== 'GK' && !excluded.has(player.id),
      )
      .sort(
        (a, b) =>
          a.position.distanceToSquared(target) - b.position.distanceToSquared(target),
      )[0] ?? null
  );
}

function bestSwitchTarget(
  match: Match,
  teamId: number,
  controlled: ReadonlySet<number>,
): MatchPlayer | null {
  const team = match.team(teamId);
  const designated = match.designatedPossessionPlayer;
  const action = designated.position
    .clone()
    .multiplyScalar(0.5)
    .addScaledVector(match.ball.predict(100, new Vector3()).setZ(0), 0.5);
  const defense = new Vector3(action.x + team.side * 4, action.y * 0.8, 0);
  const offense = new Vector3(action.x - team.side * 8, action.y * 0.8, 0);
  const defense2 = defense
    .multiplyScalar(0.8)
    .addScaledVector(team.designatedPlayer.position, 0.2);
  let offenseBias =
    (team.fadingPossessionAmount - 0.5) * 0.2 +
    clamp(team.possessionAmount - 0.5, 0, 1) * 0.8;
  offenseBias = clamp((offenseBias - 0.5) * 2 + 0.5, 0, 1);
  offenseBias = clamp(Math.pow(offenseBias, 1.5), 0, 1);
  const target = defense2
    .multiplyScalar(1 - offenseBias)
    .addScaledVector(offense, offenseBias);
  const candidates = team.players
    .filter(
      (player) =>
        player.active && player.role !== 'GK' && !controlled.has(player.id),
    )
    .sort(
      (a, b) =>
        a.position.distanceToSquared(target) - b.position.distanceToSquared(target),
    )
    .slice(0, 8);
  if (designated.teamId === teamId) return candidates[0] ?? null;

  const ownGoal = new Vector3(team.side * 55, 0, 0);
  const opponentDistance = ownGoal.distanceTo(
    designated.position.clone().addScaledVector(designated.movement, 0.5),
  );
  return (
    candidates.find(
      (player) =>
        ownGoal.distanceTo(
          player.position.clone().addScaledVector(player.movement, 0.5),
        ) <
        opponentDistance + 1 + clamp(opponentDistance * 0.1, 0, 3),
    ) ??
    candidates[0] ??
    null
  );
}

/**
 * Squeeze the man on the ball, `teamAIcontroller.cpp:930-945`.
 *
 * Picks the team-mate nearest a point just goal-side of where the opponent is heading, sends
 * him at the ball, and hands him that opponent to mark. The keeper is never picked, and nor is
 * a player somebody is already driving — the order is for the computer-controlled ten.
 */
export function applyTeamPressure(match: Match, teamId: number, controlled: ReadonlySet<number>): void {
  const team = match.team(teamId);
  const opponent = match.opponentTeam(teamId).designatedPlayer;
  const spot = opponent.position
    .clone()
    .addScaledVector(opponent.movement, 0.24)
    .add(new Vector3(team.side, 0, 0));

  const candidate = team.players
    .filter((player) => player.active && player.role !== 'GK' && !controlled.has(player.id))
    .sort((a, b) => a.position.distanceToSquared(spot) - b.position.distanceToSquared(spot))[0];

  team.teamPressureUntilMs = match.timeMs + 500;
  if (!candidate) return;
  team.teamPressurePlayer = candidate;
  candidate.manMarkingId = opponent.id;
}

/** How close he has to be to get a hand on the shirt. Arm's length plus a stride. */
const SHIRT_PULL_RANGE = 1.6;
/**
 * How hard the tug is, in metres per tick.
 *
 * Applied through the same displacement a collision uses (`offsetPosition`), so it is not a
 * special case bolted on: it drags him bodily backwards, it counts towards how unbalanced the
 * engine thinks he is, and everything that already reads that — how much power he can get
 * through the ball, how badly his aim suffers — answers to it for free. The collision pass caps
 * its own shove at eight centimetres a tick; a shirt is a quarter of that, which is a drag rather
 * than a shove. Measured over six seeds, six tenths of a second of it takes a man from 0.95 m
 * of ground gained to 0.02 m: he is stopped, not thrown.
 */
const SHIRT_PULL_METRES_PER_TICK = 0.02;

/**
 * Take hold of the nearest opponent's shirt, and be booked for it if it is cynical.
 *
 * The engine has no offence that is not a tackle — everything it punishes comes out of two
 * men's legs overlapping — so this is a foul the referee has to be told about rather than one
 * he can see. He is told once per hold, not once per tick.
 *
 * The card is football's own rule and not a scale: a free kick for holding, and a caution when
 * the man being held has the ball and there is nobody of the puller's side goal-side of him,
 * which is what "stopping a promising attack" means.
 */
export function applyShirtPull(match: Match, puller: MatchPlayer): MatchPlayer | null {
  const victim = nearestOpponentWithin(match, puller, SHIRT_PULL_RANGE, puller.directionVec);
  if (!victim) return null;

  // BACKWARDS ALONG HIS OWN RUN, not towards the man holding him, and the difference is not
  // cosmetic: dragging him towards the puller makes the two bodies overlap, and the collision
  // pass shoves them apart again on the same tick. Measured that way over six seeds, the man
  // being held covered MORE ground than the man nobody touched. Retarding him instead is also
  // what a shirt actually does — it does not pull you sideways, it stops you going.
  const back = victim.directionVec.clone().setZ(0);
  if (back.lengthSq() > 0) {
    victim.anim.offsetPosition?.(back.normalize().multiplyScalar(-SHIRT_PULL_METRES_PER_TICK));
  }

  const ownGoal = new Vector3(match.team(puller.teamId).side * PITCH.halfLength, 0, 0);
  const victimToGoal = victim.position.distanceTo(ownGoal);
  const covered = match
    .team(puller.teamId)
    .players.some(
      (mate) => mate !== puller && mate.active && mate.position.distanceTo(ownGoal) < victimToGoal,
    );
  match.referee.offenceNotice(match, victim, puller, victim.hasPossession && !covered ? 2 : 1);
  return victim;
}

/** Send the keeper out at the ball for three tenths of a second. `:947-949`. */
export function applyKeeperRush(match: Match, teamId: number): void {
  match.team(teamId).keeperRushUntilMs = match.timeMs + 300;
}

/**
 * All out: for as long as the key is down, this side stops playing the percentages.
 *
 * The third order to the rest of the side and the only one that goes to all of them at once.
 * What it changes is three thresholds and nothing else — how far a man will leave his shape to
 * close somebody down, how likely a challenge has to look before he makes it, and how sure he
 * has to be of winning the ball before he goes to ground for it. See `underAllOutOrder` in
 * `src/sim/controller.ts` for the numbers and for why it can only ever reach one side.
 *
 * IT DOES NOT MAKE THEM VIOLENT. The punch and the flying kick are a person's own acts and
 * stay on his own keys: ten men going berserk on one tap would be uncontrollable, and the
 * decision that the computer never starts violence is the owner's and is not open. What they do
 * instead is what a side chasing a game does — go in harder, and go in more often.
 *
 * Half a second, like the squeeze, because it is meant to be held down.
 */
export function applyAllOut(match: Match, teamId: number): void {
  match.team(teamId).allOutUntilMs = match.timeMs + 500;
}

function applyAttackingRun(match: Match, owner: MatchPlayer): void {
  const team = match.team(owner.teamId);
  const target = team.players
    .filter((player) => player.active && player !== owner && player.role !== 'GK')
    .sort(
      (a, b) =>
        a.position.distanceToSquared(owner.position) -
        b.position.distanceToSquared(owner.position),
    )[0];
  if (!target) return;
  team.attackingRunPlayer = target;
  team.attackingRunUntilMs = match.timeMs + 3000;
}

/**
 * How long a press of the short-pass key, made while a nomination is live, still counts as the
 * give half of a wall pass.
 *
 * Measured from the press, not from the strike: holding the key down charges its power first
 * (`captureAction`, `currentAction` above), so this window has to cover that charge on top of
 * a short ball's own flight. Generous for both, and still short enough that the order cannot
 * survive losing the ball and then reattach itself to some unrelated later possession.
 * `wallPassReturnCommand` (`src/sim/controller.ts`) is what actually spends it, and lets it
 * lapse unconsumed rather than clearing it explicitly: an unreachable return is simply never
 * offered, and a clip already in flight cannot be re-queued mid-touch, so nothing can fire
 * twice.
 */
const WALL_PASS_RETURN_WINDOW_MS = 2000;

/**
 * The second half of a wall pass ("pared"): give it, sprint into space, get it back first time.
 *
 * `applyAttackingRun` above is the first half and already existed — SEND HIM ON A RUN. This is
 * what turns that nomination into a genuine give-and-go: called on the rising edge of the
 * short-pass key, while the passer's own nomination on a team-mate is still live, it tells
 * that team-mate, through the two team fields below, to return the ball the moment it is his
 * rather than trap it. `wallPassReturnCommand` is what actually offers him that pass, and it
 * only ever fires from here — never from the computer's own play, which has no call to this
 * function at all.
 */
function armWallPassReturn(match: Match, passer: MatchPlayer): void {
  const target = liveAttackingRunTarget(match, passer);
  if (!target) return;
  const team = match.team(passer.teamId);
  team.wallPassReceiver = target;
  team.wallPassPasser = passer;
  team.wallPassUntilMs = match.timeMs + WALL_PASS_RETURN_WINDOW_MS;
}
